import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import { buttonVariants } from '@/components/ui/button'
import { BookButton, useBooking } from '@/components/landing/booking-provider'

import {
    Ambulance,
    CalendarPlus,
    ChevronRight,
    CircleCheck,
    ShieldCheck,
    Stethoscope
} from 'lucide-react'

import {
    clinic,
    serviceOrder,
    services,
    stats
} from '@/constants/clinic'

const highlights = [
    'Tanpa perlu membuat akun',
    'Peserta JKN dilayani',
    'UGD buka 24 jam'
]

const QuickBookingCard: FC = (): ReactNode => {
    const booking = useBooking()

    return (
        <div className='rounded-3xl bg-white/95 p-5 shadow-2xl shadow-brand-950/15 ring-1 ring-black/5 backdrop-blur sm:p-6'>
            <p className='text-[11px] font-bold tracking-[0.16em] text-brand-700 uppercase'>
                Untuk janji online
            </p>

            <p className='mt-1.5 text-lg font-bold text-ink'>
                Layanan rawat jalan
            </p>

            <p className='text-sm text-muted-foreground'>
                Pilih poli sesuai kebutuhan Anda.
            </p>

            <ul className='mt-4 grid gap-2'>
                {serviceOrder.map(id => {
                    const item = services[id]
                    const Icon = item.icon

                    const inner = (
                        <>
                            <span className='flex size-10 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-brand-700 ring-1 ring-brand-100'>
                                <Icon className='size-5' aria-hidden='true' />
                            </span>

                            <span className='min-w-0 flex-1'>
                                <span className='block text-sm font-bold text-ink'>
                                    {item.name}
                                </span>

                                <span className='block truncate text-xs text-muted-foreground'>
                                    {item.short}
                                </span>
                            </span>

                            <ChevronRight className='size-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-brand-600' aria-hidden='true' />
                        </>
                    )

                    const itemClass = 'group flex w-full items-center gap-3 rounded-2xl border border-border bg-white px-3 py-2.5 text-left transition-all outline-none hover:border-brand-300 hover:bg-brand-50/50 focus-visible:ring-4 focus-visible:ring-ring/30'

                    return (
                        <li key={id}>
                            {booking ? (
                                <button
                                    type='button'
                                    onClick={() => booking.openBooking({ service: id })}
                                    className={itemClass}
                                    aria-label={`Buat janji ${item.name}`}
                                >
                                    {inner}
                                </button>
                            ) : (
                                <a href='/#ketersediaan' className={itemClass}>
                                    {inner}
                                </a>
                            )}
                        </li>
                    )
                })}
            </ul>
        </div>
    )
}

const HeroSection: FC = (): ReactNode => {
    return (
        <section id='beranda' aria-labelledby='hero-title' className='relative isolate overflow-hidden'>
            <div className='pointer-events-none absolute inset-0 -z-10' aria-hidden='true'>
                <div className='absolute inset-0 bg-linear-to-b from-brand-50 via-white to-background' />
                <div className='absolute -top-48 -right-40 size-[38rem] rounded-full bg-brand-200/45 blur-3xl' />
                <div className='absolute top-1/3 -left-48 size-[30rem] rounded-full bg-sky-100/70 blur-3xl' />
                <div className='absolute inset-0 bg-dots opacity-50 [mask-image:radial-gradient(ellipse_at_top_left,black,transparent_65%)]' />
            </div>

            <div className='mx-auto grid max-w-7xl items-center gap-14 px-4 pt-10 pb-14 sm:px-6 sm:pt-14 lg:grid-cols-[1.05fr_1fr] lg:gap-12 lg:px-8 lg:pt-16 lg:pb-24'>
                <div className='animate-in duration-700 ease-out fade-in slide-in-from-bottom-4 motion-reduce:animate-none'>
                    <p className='inline-flex items-center gap-2 rounded-full bg-white px-3.5 py-1.5 text-[13px] font-semibold text-emerald-800 shadow-sm ring-1 ring-emerald-200/80'>
                        <ShieldCheck className='size-4 shrink-0 text-emerald-600' aria-hidden='true' />
                        {clinic.accreditation}
                    </p>

                    <h1
                        id='hero-title'
                        className='mt-6 text-[2.35rem] leading-[1.08] font-extrabold tracking-tight text-balance text-ink sm:text-5xl lg:text-[3.5rem]'
                    >
                        Layanan kesehatan tingkat pertama di
                        {' '}
                        <span className='box-decoration-clone bg-linear-to-t from-brand-100 from-[34%] to-transparent to-[34%] text-brand-600'>
                            Leuwigajah, Cimahi.
                        </span>
                    </h1>

                    <p className='mt-6 max-w-xl text-lg leading-relaxed text-ink-soft'>
                        Buat janji dengan dokter secara online dan cek kuota layanan sebelum datang. Melayani pasien umum dan peserta JKN.
                    </p>

                    <div className='mt-8 flex flex-col gap-3 sm:flex-row'>
                        <BookButton size='lg'>
                            <CalendarPlus className='size-5' aria-hidden='true' />
                            Buat Janji
                        </BookButton>

                        <a href='#jadwal-dokter' className={buttonVariants({ variant: 'outline', size: 'lg' })}>
                            <Stethoscope className='size-5 text-brand-600' aria-hidden='true' />
                            Lihat jadwal dokter
                        </a>
                    </div>

                    <ul className='mt-8 flex flex-wrap gap-x-6 gap-y-3 text-sm font-medium text-ink-soft'>
                        {highlights.map(item => (
                            <li key={item} className='flex items-center gap-2'>
                                <CircleCheck className='size-4 shrink-0 text-brand-600' aria-hidden='true' />
                                {item}
                            </li>
                        ))}
                    </ul>
                </div>

                <div className='relative mx-auto w-full max-w-lg lg:mr-0 lg:max-w-none lg:pl-10'>
                    <div className='relative aspect-[4/3] overflow-hidden rounded-[2rem] bg-brand-100 shadow-2xl shadow-brand-950/15 ring-1 ring-white/70 sm:aspect-[5/4] lg:aspect-[4/5] lg:max-h-[38rem]'>
                        <Image
                            src='/images/hero-dokter.jpg'
                            alt='Dokter perempuan berjilbab dengan jas putih tersenyum ramah'
                            fill
                            priority
                            sizes='(min-width: 1024px) 40vw, (min-width: 640px) 32rem, 100vw'
                            className='object-cover object-[50%_20%]'
                        />

                        <div className='absolute inset-x-0 bottom-0 h-1/3 bg-linear-to-t from-ink/35 to-transparent' aria-hidden='true' />
                    </div>

                    <div className='absolute -top-5 -right-3 hidden animate-float-soft motion-reduce:animate-none sm:block lg:-right-4'>
                        <div className='flex items-center gap-3 rounded-2xl bg-white/95 px-4 py-3 shadow-xl shadow-ink/10 ring-1 ring-black/5 backdrop-blur'>
                            <span className='flex size-10 items-center justify-center rounded-xl bg-rose-50 text-rose-600'>
                                <Ambulance className='size-5' aria-hidden='true' />
                            </span>

                            <div>
                                <p className='text-sm font-bold text-ink'>
                                    UGD & Persalinan
                                </p>

                                <p className='flex items-center gap-1.5 text-xs font-semibold text-rose-600'>
                                    <span className='size-1.5 rounded-full bg-rose-500' aria-hidden='true' />
                                    Buka 24 jam, setiap hari
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className='relative mx-3 -mt-20 sm:mx-10 lg:absolute lg:bottom-10 lg:-left-4 lg:mx-0 lg:mt-0 lg:w-[21rem]'>
                        <QuickBookingCard />
                    </div>
                </div>
            </div>

            <div className='mx-auto max-w-7xl px-4 pb-6 sm:px-6 lg:px-8'>
                <dl className='grid grid-cols-2 gap-px overflow-hidden rounded-3xl bg-border shadow-xl shadow-brand-950/5 ring-1 ring-border lg:grid-cols-4'>
                    {stats.map(stat => {
                        const Icon = stat.icon

                        return (
                            <div key={stat.label} className='flex flex-col gap-3 bg-white p-5 sm:flex-row sm:items-center sm:gap-4 sm:p-6'>
                                <span className='flex size-11 shrink-0 items-center justify-center rounded-2xl bg-brand-50 text-brand-700 sm:size-12'>
                                    <Icon className='size-5 sm:size-6' aria-hidden='true' />
                                </span>

                                <div className='flex min-w-0 flex-col-reverse'>
                                    <dt className='text-[13px] leading-snug text-muted-foreground sm:text-sm'>
                                        {stat.label}
                                    </dt>

                                    <dd className='text-xl font-extrabold tracking-tight text-ink sm:text-2xl'>
                                        {stat.value}
                                    </dd>
                                </div>
                            </div>
                        )
                    })}
                </dl>
            </div>
        </section>
    )
}

export default HeroSection
